import React, { useState, useEffect, useRef } from 'react';

// IMPORTANT: Replace this with the actual URL of your deployed C# Cloud Function.
const GEMINI_CLOUD_FUNCTION_URL = 'https://us-central1-jqguitaracademy.cloudfunctions.net/guitarGuruFunction';

// Simple CSS-in-JS for styling. You can move this to a separate CSS file.
const styles = {
  chatContainer: {
    fontFamily: 'sans-serif',
    width: '100%',
    maxWidth: '500px',
    height: '600px',
    margin: '20px auto',
    border: '1px solid #ddd',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
  messageList: {
    flex: 1,
    padding: '20px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  message: {
    padding: '10px 15px',
    borderRadius: '18px',
    maxWidth: '80%',
    lineHeight: '1.5',
  },
  userMessage: {
    backgroundColor: '#007bff',
    color: 'white',
    alignSelf: 'flex-end',
    borderBottomRightRadius: '4px',
  },
  botMessage: {
    backgroundColor: '#f1f1f1',
    color: '#333',
    alignSelf: 'flex-start',
    borderBottomLeftRadius: '4px',
  },
  errorMessage: {
    backgroundColor: '#fff0f0',
    color: '#d93025',
    border: '1px solid #d93025',
  },
  chatInputContainer: {
    display: 'flex',
    padding: '10px',
    borderTop: '1px solid #ddd',
    backgroundColor: '#fff',
  },
  chatInput: {
    flex: 1,
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '20px',
    marginRight: '10px',
    fontSize: '16px',
  },
  sendButton: {
    padding: '10px 20px',
    border: 'none',
    backgroundColor: '#007bff',
    color: 'white',
    borderRadius: '20px',
    cursor: 'pointer',
    fontSize: '16px',
  },
  rateLimitMessage: {
    textAlign: 'center',
    fontSize: '12px',
    color: '#666',
    padding: '0 10px 5px',
  }
};

const GuitarGuruChat = () => {
  const [messages, setMessages] = useState([
    { text: 'Hi! I am Nigel, the Guitar Guru. Ask me anything about chords, scales, theory, or technique!', sender: 'bot' }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [lastMessageTimestamp, setLastMessageTimestamp] = useState(0);
  const messageListRef = useRef(null);

  // Automatically scroll to the bottom when new messages are added
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading || isRateLimited) return;

    // --- Client-Side Rate Limiting (1 message per 30 seconds -> 2 per minute) ---
    const now = Date.now();
    if (now - lastMessageTimestamp < 30000) {
      setIsRateLimited(true);
      // Set a timer to remove the rate limit message
      setTimeout(() => setIsRateLimited(false), 30000 - (now - lastMessageTimestamp));
      return;
    }

    const userMessage = { text: input, sender: 'user' };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setLastMessageTimestamp(Date.now());

    try {
      // Call the secure backend Cloud Function
      const response = await fetch(GEMINI_CLOUD_FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: input }),
      });

      if (!response.ok) {
        // Handle the "busy" message from our global rate limiter
        if (response.status === 429) { // 429 Too Many Requests
          throw new Error('Nigel, The Guitar Guru is busy with other students right now. Please try again in a moment.');
        }
        throw new Error('Sorry, something went wrong on my end. Please try again.');
      }

      const data = await response.json();
      const botMessage = { text: data.response, sender: 'bot' };
      setMessages(prev => [...prev, botMessage]);

    } catch (error) {
      const errorMessage = { text: error.message, sender: 'bot', isError: true };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={styles.chatContainer}>
      <div style={styles.messageList} ref={messageListRef}>
        {messages.map((msg, index) => (
          <div
            key={index}
            style={{
              ...styles.message,
              ...(msg.sender === 'user' ? styles.userMessage : styles.botMessage),
              ...(msg.isError ? styles.errorMessage : {}),
            }}
          >
            {msg.text}
          </div>
        ))}
      </div>
      <div>
        <div style={styles.chatInputContainer}>
          <input
            type="text"
            style={styles.chatInput}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            placeholder="Ask about a G chord..."
            disabled={isLoading || isRateLimited}
          />
          <button
            style={styles.sendButton}
            onClick={handleSendMessage}
            disabled={isLoading || isRateLimited}
          >
            {isLoading ? '...' : 'Send'}
          </button>
        </div>
        {isRateLimited && <p style={styles.rateLimitMessage}>You can ask another question in a few moments.</p>}
      </div>
    </div>
  );
};

export default GuitarGuruChat;
